import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import { onLoadingChange } from "../api";
import { isValidDateRange, periodRange, previousPeriodRange } from "../analytics";
import { PERIOD_PRESETS } from "../constants";
import { sameDateRange } from "../dashboardFilters";

export { alignPeriods } from "../analytics";

export const ThemeCtx = createContext({});
export const useT = () => useContext(ThemeCtx);

export const LabelCtx = createContext({ show: false, size: 11 });
export const useShowLabels = () => useContext(LabelCtx).show;
export const useLabelSize = () => useContext(LabelCtx).size;

const finite = value => Number.isFinite(Number(value)) ? Number(value) : 0;
export const fmtNum = value => Math.round(finite(value)).toLocaleString("ko-KR");
export const fmtKRW = value => `₩${fmtNum(value)}`;
export const fmtUSD = value => `$${finite(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtECPM = fmtUSD;
export const fmtPct = (value, digits = 1) => `${finite(value).toFixed(digits)}%`;

export const fmtLabelKRW = value => value ? fmtKRW(value) : "";
export const fmtLabelUSD = value => value ? fmtUSD(value) : "";
export const fmtLabelECPM = value => value ? fmtECPM(value) : "";
export const fmtLabelNum = value => value ? fmtNum(value) : "";

export function SmartLabelList({ dataKey, formatter, size, color, position = "top", style }) {
  const show = useShowLabels();
  const contextSize = useLabelSize();
  if (!show) return null;
  return (
    <LabelList
      dataKey={dataKey}
      formatter={formatter}
      position={position}
      fill={color}
      fontSize={size || contextSize}
      style={{ fontWeight: 600, ...style }}
    />
  );
}

export function calcDomain(data, keys, { includeZero = true } = {}) {
  const values = (data || []).flatMap(row => keys.map(key => Number(row?.[key]))).filter(Number.isFinite);
  if (!values.length) return [0, "auto"];
  const rawMin = Math.min(...values);
  const min = includeZero ? Math.min(0, rawMin) : rawMin;
  const max = Math.max(...values);
  if (min === max) return [min, max || 1];
  const padding = (max - min) * 0.08;
  const lower = includeZero && rawMin >= 0 ? 0 : Math.floor(min - padding);
  return [lower, Math.ceil(max + padding)];
}

export function KpiCard({ label, value, sub, accent, icon }) {
  return (
    <article className="kpi-card" style={{ "--kpi-accent": accent }}>
      <div className="kpi-card__top">
        <span className="kpi-card__label">{label}</span>
        {icon && <span className="kpi-card__icon" aria-hidden="true">{icon}</span>}
      </div>
      <strong className="kpi-card__value">{value}</strong>
      {sub && <span className="kpi-card__sub">{sub}</span>}
    </article>
  );
}

export function Card({ children, style, className = "" }) {
  return <section className={`dashboard-card ${className}`.trim()} style={style}>{children}</section>;
}

export function SectionTitle({ children, action, eyebrow }) {
  return (
    <div className="section-heading">
      <div>
        {eyebrow && <span className="section-heading__eyebrow">{eyebrow}</span>}
        <h2 className="section-heading__title">{children}</h2>
      </div>
      {action && <div className="section-heading__action">{action}</div>}
    </div>
  );
}

export function ChartTip({ active, payload, label, fmt = value => value }) {
  const T = useT();
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip" style={{ background: T.tooltipBg, borderColor: T.tooltipBorder }}>
      {label != null && <div className="chart-tooltip__label">{label}</div>}
      {payload.filter(item => item.value != null).map((item, index) => (
        <div key={`${item.dataKey || item.name}-${index}`} className="chart-tooltip__row" style={{ color: item.color || item.stroke || T.textSub }}>
          <span>{item.name || item.dataKey}</span>
          <strong>{fmt(item.value, item)}</strong>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ msg = "표시할 데이터가 없습니다.", title = "데이터가 없습니다" }) {
  return (
    <div className="empty-state" role="status">
      <span className="empty-state__icon" aria-hidden="true">—</span>
      <strong>{title}</strong>
      <span>{msg}</span>
    </div>
  );
}

export function StatusBanner({ children, type = "info", action, compact = false }) {
  return (
    <div className={`status-banner status-banner--${type}${compact ? " is-compact" : ""}`} role={type === "error" ? "alert" : "status"}>
      <span className="status-banner__icon" aria-hidden="true">{type === "error" ? "!" : type === "warning" ? "△" : "i"}</span>
      <span className="status-banner__message">{children}</span>
      {action && <span className="status-banner__action">{action}</span>}
    </div>
  );
}

export function FilterBtn({ active, onClick, children, disabled, title }) {
  return (
    <button type="button" className={`filter-chip${active ? " is-active" : ""}`} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

export function HoverRow({ children, selected, onClick }) {
  return (
    <tr className={`${onClick ? "is-clickable " : ""}${selected ? "is-selected" : ""}`.trim()} onClick={onClick}>
      {children}
    </tr>
  );
}

export function Select({ value, onChange, children, style, ...props }) {
  return (
    <select className="ui-control ui-select" value={value} onChange={event => onChange(event.target.value)} style={style} {...props}>
      {children}
    </select>
  );
}

export function Input({ value, onChange, placeholder, type = "text", style, ...props }) {
  return (
    <input className="ui-control ui-input" type={type} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} style={style} {...props} />
  );
}

export function Btn({ onClick, children, variant = "primary", disabled, style, type = "button", ...props }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`ui-btn ui-btn--${variant}`} style={style} {...props}>
      {children}
    </button>
  );
}

export const tdS = { padding: "10px 14px", fontSize: 13 };
export const tdSm = { padding: "9px 12px", fontSize: 12.5 };

export function commonChartProps(T) {
  return {
    cartesianGrid: <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} vertical={false} />,
    xAxis: dataKey => <XAxis dataKey={dataKey} tick={{ fill: T.tickTxt, fontSize: 11 }} tickLine={false} axisLine={{ stroke: T.gridLine }} />,
    yAxis: formatter => <YAxis tick={{ fill: T.tickTxt, fontSize: 11 }} tickFormatter={formatter} tickLine={false} axisLine={false} width={88} />,
  };
}

export function usePeriodRange() {
  const [days, setDays] = useState(null);
  const [useCustom, setUseCustom] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [draftPeriodCompare, setDraftPeriodCompare] = useState(false);
  const [daysB, setDaysB] = useState(null);
  const [useCustomB, setUseCustomB] = useState(false);
  const [draftFromB, setDraftFromB] = useState("");
  const [draftToB, setDraftToB] = useState("");
  const [refDate, setRefDate] = useState(null);
  const [rangeA, setRangeA] = useState({ from: "", to: "" });
  const [rangeB, setRangeB] = useState({ from: "", to: "" });
  const [periodCompare, setPeriodCompare] = useState(false);

  const draftRangeA = useMemo(
    () => useCustom
      ? (isValidDateRange(draftFrom, draftTo) ? { from: draftFrom, to: draftTo } : { from: "", to: "" })
      : (days ? periodRange(days, refDate || undefined) : { from: "", to: "" }),
    [days, useCustom, draftFrom, draftTo, refDate],
  );
  const draftRangeB = useMemo(
    () => useCustomB
      ? (isValidDateRange(draftFromB, draftToB) ? { from: draftFromB, to: draftToB } : { from: "", to: "" })
      : (daysB ? periodRange(daysB, refDate || undefined) : { from: "", to: "" }),
    [daysB, useCustomB, draftFromB, draftToB, refDate],
  );

  const validDraftA = isValidDateRange(draftRangeA.from, draftRangeA.to);
  const validDraftB = isValidDateRange(draftRangeB.from, draftRangeB.to);
  const hasPending = !sameDateRange(draftRangeA, rangeA)
    || draftPeriodCompare !== periodCompare
    || (draftPeriodCompare && !sameDateRange(draftRangeB, rangeB));

  const applyDraft = (forceSinglePeriod = false) => {
    const nextPeriodCompare = forceSinglePeriod ? false : draftPeriodCompare;
    if (!validDraftA || (nextPeriodCompare && !validDraftB)) return false;
    setRangeA({ ...draftRangeA });
    setRangeB({ ...draftRangeB });
    setPeriodCompare(nextPeriodCompare);
    if (forceSinglePeriod) setDraftPeriodCompare(false);
    return true;
  };

  function togglePeriodCompare() {
    setDraftPeriodCompare(previous => {
      if (!previous) {
        if (isValidDateRange(draftRangeA.from, draftRangeA.to)) {
          const previousRange = previousPeriodRange(draftRangeA);
          setDaysB(days);
          setUseCustomB(true);
          setDraftFromB(previousRange.from);
          setDraftToB(previousRange.to);
        }
      }
      return !previous;
    });
  }

  return {
    days, setDays, useCustom, setUseCustom,
    draftFrom, setDraftFrom, draftTo, setDraftTo, draftRangeA,
    periodCompare, setPeriodCompare, togglePeriodCompare,
    draftPeriodCompare, setDraftPeriodCompare,
    daysB, setDaysB, useCustomB, setUseCustomB,
    draftFromB, setDraftFromB, draftToB, setDraftToB, draftRangeB,
    applyDraft, hasPending, validDraftA, validDraftB,
    rangeA, rangeB, refDate, setRefDate,
  };
}

function useProgressBar(loading) {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const progressTimer = useRef(null);
  const elapsedTimer = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    clearInterval(progressTimer.current);
    clearInterval(elapsedTimer.current);
    clearTimeout(hideTimer.current);

    if (loading) {
      setProgress(8);
      setElapsed(0);
      setVisible(true);
      progressTimer.current = setInterval(() => setProgress(value => value >= 90 ? 90 : value + Math.max(1, (90 - value) * 0.08)), 280);
      elapsedTimer.current = setInterval(() => setElapsed(value => value + 1), 1000);
    } else if (visible) {
      setProgress(100);
      hideTimer.current = setTimeout(() => { setVisible(false); setProgress(0); }, 350);
    }

    return () => {
      clearInterval(progressTimer.current);
      clearInterval(elapsedTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, [loading, visible]);

  return { progress, visible, elapsed };
}

function PeriodRow({ activeDays, custom, onDay, onCustom, from, setFrom, to, setTo, label }) {
  return (
    <div className="period-row">
      {label && <span className="period-row__label">{label}</span>}
      <div className="period-row__presets">
        {PERIOD_PRESETS.map(day => (
          <FilterBtn key={day} active={!custom && activeDays === day} onClick={() => onDay(day)}>{day}일</FilterBtn>
        ))}
        <FilterBtn active={custom} onClick={onCustom}>직접 선택</FilterBtn>
      </div>
      {custom && (
        <div className="period-row__custom">
          <Input aria-label={`${label || "기간"} 시작일`} type="date" value={from} onChange={setFrom} />
          <span aria-hidden="true">–</span>
          <Input aria-label={`${label || "기간"} 종료일`} type="date" value={to} onChange={setTo} />
        </div>
      )}
    </div>
  );
}

export function PeriodBar({ pr, compareMode, loading, extra }) {
  const { progress, visible, elapsed } = useProgressBar(loading);
  const rowA = {
    activeDays: pr.days,
    custom: pr.useCustom,
    onDay: day => { pr.setDays(day); pr.setUseCustom(false); },
    onCustom: () => {
      pr.setDraftFrom(pr.draftRangeA.from || pr.rangeA.from);
      pr.setDraftTo(pr.draftRangeA.to || pr.rangeA.to);
      pr.setUseCustom(true);
    },
    from: pr.draftFrom, setFrom: pr.setDraftFrom, to: pr.draftTo, setTo: pr.setDraftTo,
  };
  const rowB = {
    activeDays: pr.daysB,
    custom: pr.useCustomB,
    onDay: day => { pr.setDaysB(day); pr.setUseCustomB(false); },
    onCustom: () => {
      pr.setDraftFromB(pr.draftRangeB.from || pr.rangeB.from);
      pr.setDraftToB(pr.draftRangeB.to || pr.rangeB.to);
      pr.setUseCustomB(true);
    },
    from: pr.draftFromB, setFrom: pr.setDraftFromB, to: pr.draftToB, setTo: pr.setDraftToB,
  };

  const targetCompareMode = pr.draftTargetCompareMode ?? compareMode;
  const showPeriodCompare = !targetCompareMode && pr.draftPeriodCompare;

  return (
    <section className="period-panel" aria-label="분석 기간">
      <div className="period-panel__header">
        <div className="period-panel__title">
          <span className="period-panel__icon" aria-hidden="true">◷</span>
          <span>분석 기간</span>
        </div>
        {!targetCompareMode && (
          <FilterBtn active={pr.draftPeriodCompare} onClick={pr.togglePeriodCompare}>기간 비교</FilterBtn>
        )}
        {loading && <span className="loading-inline"><span />데이터 불러오는 중{elapsed ? ` · ${elapsed}초` : ""}</span>}
        {pr.hasPendingAll && <span className="period-panel__pending">변경사항 미적용</span>}
        {extra && <div className="period-panel__extra">{extra}</div>}
        <Btn onClick={pr.applyAll} disabled={!pr.canApplyAll || !pr.hasPendingAll || loading}>적용</Btn>
      </div>
      <div className="period-panel__body">
        <PeriodRow {...rowA} label={showPeriodCompare ? "A" : null} />
        {showPeriodCompare && <PeriodRow {...rowB} label="B" />}
      </div>
      {visible && <div className="progress-track"><div style={{ width: `${progress}%` }} /></div>}
    </section>
  );
}

export function LoadingOverlay() {
  const [loading, setLoading] = useState(false);
  const { progress, visible, elapsed } = useProgressBar(loading);

  useEffect(() => onLoadingChange(setLoading), []);
  if (!visible) return null;

  return (
    <div className="loading-overlay" role="status" aria-live="polite">
      <div className="loading-overlay__panel">
        <span className="loading-spinner" aria-hidden="true" />
        <strong>데이터를 준비하고 있습니다</strong>
        <span>{elapsed ? `${elapsed}초 경과` : "잠시만 기다려 주세요"}</span>
        <div className="progress-track"><div style={{ width: `${progress}%` }} /></div>
      </div>
    </div>
  );
}
